import { TimelineAreaFragment, TimelineFullSiteFragment, TimelineRoomFragment } from '@/generated/graphql';
import { routeForLocation } from '@/location/location';
import { $t } from '@/plugins/fluent';
import { computed, Ref, toValue, watchEffect } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { getSectionId } from '../timeline';
import { isLocationType, LocationType, LocationTypeCategory } from '../Timeline.types';

export type TimelineLocationData = {
    id: string;
    label: string;
    /** Nesting depth, which the first-column cells render as indentation: 0 = none, 1 = one step, ... */
    level: number;
    typeCategory: LocationTypeCategory;
    isLeafLocation: boolean;
    status: 'Strong';
    nestedLocations: TimelineLocationData[];
    // Only present for rooms/areas (leaf locations with sections)
    linkTarget?: RouteLocationRaw;
    type?: LocationType;
    sectionId?: string;
};

/**
 * Levels that exist purely to contain other locations. Only these are incomplete when they have no
 * children — a building without floors, or a zone without areas, is a data problem worth surfacing.
 *
 * Levels that hold allocations are deliberately excluded. An area is the outdoor equivalent of a room and
 * is where allocations live; it nests plots, but it may legitimately have none, so warning there reported a
 * data error for a perfectly normal area. The same will apply to rooms once they nest partitions.
 */
const CONTAINER_TYPE_CATEGORIES: LocationTypeCategory[] = ['Building', 'Floor', 'Zone'];

// A container location with no children is invalid — surface the same warning the props-based path showed.
export function nestedLocationsErrorMessage(location: TimelineLocationData): string | undefined {
    if (!CONTAINER_TYPE_CATEGORIES.includes(location.typeCategory)) {
        return undefined;
    }

    return location.nestedLocations.length === 0 ? $t('error-nested-locations-missing') : undefined;
}

/**
 * Keeps only the leaves in `leafIds` and drops every parent location left without any. The hierarchy view is
 * pruned to the locations the sections view shows, so both views offer the same copy targets.
 */
function pruneToLeaves(locations: TimelineLocationData[], leafIds: Set<string>): TimelineLocationData[] {
    return locations.flatMap((location) => {
        if (location.isLeafLocation) {
            return leafIds.has(location.id) ? [location] : [];
        }
        const nestedLocations = pruneToLeaves(location.nestedLocations, leafIds);
        return nestedLocations.length > 0 ? [{ ...location, nestedLocations }] : [];
    });
}

export function useTimelineLocations(
    siteRef: Ref<TimelineFullSiteFragment>,
    roomsRef: Ref<TimelineRoomFragment[]>,
    areasRef: Ref<TimelineAreaFragment[]>,
    showSections: Ref<boolean>,
) {
    const rooms = computed<TimelineLocationData[]>(() =>
        toValue(roomsRef).map((room) => {
            const type = isLocationType(room.type?.name) ? room.type.name : undefined;
            return {
                id: room.id,
                label: room.nameShort,
                linkTarget: routeForLocation('Room', room.id),
                level: 0,
                type,
                typeCategory: 'Room' as LocationTypeCategory,
                isLeafLocation: true,
                sectionId: getSectionId(type ?? 'no-room-type'),
                nestedLocations: [] as TimelineLocationData[],
                status: 'Strong' as const, // FIXME other type here?
            } satisfies TimelineLocationData;
        }),
    );
    const areas = computed<TimelineLocationData[]>(() =>
        toValue(areasRef).map((area) => {
            const type = isLocationType(area.type?.name) ? area.type.name : undefined;
            return {
                id: area.id,
                label: area.nameShort,
                linkTarget: routeForLocation('Area', area.id),
                level: 0,
                type,
                typeCategory: 'Area' as LocationTypeCategory,
                isLeafLocation: true,
                sectionId: getSectionId(type ?? 'no-area-type'),
                nestedLocations: [] as TimelineLocationData[],
                status: 'Strong' as const, // FIXME other type here?
            } satisfies TimelineLocationData;
        }),
    );

    const buildings = computed<TimelineLocationData[]>(() =>
        toValue(siteRef).buildings.nodes.map(
            (building) =>
                ({
                    id: building.id,
                    label: building.nameShort,
                    level: 0,
                    typeCategory: 'Building' as LocationTypeCategory,
                    isLeafLocation: false,
                    status: 'Strong' as const, // FIXME other type here?
                    nestedLocations: building.levels.nodes.map(
                        (level) =>
                            ({
                                id: level.id,
                                label: level.nameShort,
                                level: 1,
                                typeCategory: 'Floor' as LocationTypeCategory,
                                isLeafLocation: false,
                                status: 'Strong' as const, // FIXME other type here?
                                nestedLocations: level.rooms.nodes.map(
                                    (room) =>
                                        ({
                                            id: room.id,
                                            label: room.nameShort,
                                            level: 2,
                                            type: isLocationType(room.type?.name) ? room.type.name : undefined,
                                            typeCategory: 'Room' as LocationTypeCategory,
                                            isLeafLocation: true,
                                            nestedLocations: [] as TimelineLocationData[],
                                            status: 'Strong' as const, // FIXME other type here?
                                        }) satisfies TimelineLocationData,
                                ),
                            }) satisfies TimelineLocationData,
                    ),
                }) satisfies TimelineLocationData,
        ),
    );

    const zones = computed<TimelineLocationData[]>(() =>
        toValue(siteRef).zones.nodes.map(
            (zone) =>
                ({
                    id: zone.id,
                    label: zone.nameShort,
                    level: 0,
                    typeCategory: 'Zone' as LocationTypeCategory,
                    isLeafLocation: false,
                    status: 'Strong' as const, // FIXME other type here?
                    nestedLocations: zone.areas.nodes.map(
                        (area) =>
                            ({
                                id: area.id,
                                label: area.nameShort,
                                level: 1,
                                type: isLocationType(area.type?.name) ? area.type.name : undefined,
                                typeCategory: 'Area' as LocationTypeCategory,
                                isLeafLocation: true,
                                nestedLocations: [] as TimelineLocationData[],
                                status: 'Strong' as const, // FIXME other type here?
                            }) satisfies TimelineLocationData,
                    ),
                }) satisfies TimelineLocationData,
        ),
    );

    function getLocations() {
        if (toValue(showSections)) {
            return computed(() => toValue(rooms).concat(toValue(areas)));
        }
        return computed(() => {
            const sectionLocationIds = new Set([...toValue(rooms), ...toValue(areas)].map((location) => location.id));
            return pruneToLeaves(toValue(buildings).concat(toValue(zones)), sectionLocationIds);
        });
    }

    watchEffect(() => getLocations());

    return {
        getLocations,
    };
}
